import React, {useCallback, useMemo} from "react";
import {PrintBrochureReviewDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, DataGridDensities, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {ReactComponent as NoColorIcon} from "../../../../../../../../assets/images/notifications/no-color.svg";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {Box, Card, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";


const dataGridSaveConfig = {
    name: "PrintBrochureViewReviewSectionDataGrid",
    version: 1,
}

const PrintBrochureViewReviewSection = ({
                                            reviewState,
                                            updatingStep,
                                        }) => {

    const columns = useMemo(() =>
            /**@type {DataGridColumn[]}*/
            [
                {
                    title: 'Part No',
                    name: PrintBrochureReviewDataGridColumnNames.partNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    resizable: true,
                    width: {type: DataGridColumnWidthTypes.flex, size: 1, minWidth: 210},
                },
                {
                    title: 'Note',
                    name: PrintBrochureReviewDataGridColumnNames.note,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    resizable: false,
                    width: {type: DataGridColumnWidthTypes.flex, size: 2, minWidth: 250},
                },
            ]
        , [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });


    /**
     * Creates the rows for the data grid.
     * @type {(function(*): DataGridRow[])}
     * */
    const dataGridRows = useCallback((group) => {
        return group.products?.map((product) => {
                return {
                    key: product.id,
                    data: product,
                    cells: {
                        [PrintBrochureReviewDataGridColumnNames.partNo]:
                            <ProductInformationPopover partNo={product?.partNo}>
                                {product?.partNo ?? ''}
                            </ProductInformationPopover>,
                        [PrintBrochureReviewDataGridColumnNames.note]:
                            <Typography variant={'body2'}>
                                {product?.note ?? '-'}
                            </Typography>,
                    }
                }
            })
            ?? []
    }, [])

    return (
        <>
            <Box sx={{paddingBlockEnd: 2, paddingInline: 2, width: '100%'}}>
                <Grid2 container spacing={2} sx={{width: '100%'}}>
                    <Grid2 xs={12} className={'px-3'} sx={{display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between'}}>
                        <Typography variant={'body1'}>
                            Poster Title: <Typography variant={'body1'} component={'span'} sx={{fontWeight: 600}}>{reviewState.title ?? ""}</Typography>
                        </Typography>
                        <Typography variant={'body1'} component={'div'} sx={{display: 'flex', alignItems: 'center'}}>
                            Divider Color:
                            {
                                reviewState.dividerColorConfig?.useGroupColor &&
                                <Typography variant={'body2'} component={'span'} sx={{fontWeight: 600, marginInlineStart: 0.5}}>Follow Group Color</Typography>
                            }
                            {
                                !reviewState.dividerColorConfig?.useGroupColor &&
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 1, height: 30}}>
                                    <Box className={'color-circle cursor-not-allowed-hover'} sx={{'--color': reviewState.dividerColorConfig?.colorCode, marginBottom: '0 !important'}}>
                                        <NoColorIcon className={'placeholder'}/>
                                        <div className={'circle'}/>
                                    </Box>
                                    <Typography variant={'body2'} fontWeight={600}>
                                        {reviewState.dividerColorConfig?.colorCode ?? 'No Color'}
                                    </Typography>
                                </Box>
                            }
                        </Typography>
                    </Grid2>
                    {
                        reviewState.groups?.map((group, index) => (
                            <Grid2 xs={12} md={6} lg={4} key={group.id}>
                                <Card variant={'outlined'} sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 1, height: '100%'}} className={'no-margin-color-circle'}>
                                    <Box sx={{display: 'flex', alignItems: 'center', paddingInline: 1.5}}>
                                        <Box sx={{
                                            backgroundColor: 'background.default',
                                            borderRadius: '50%',
                                            minWidth: 24,
                                            minHeight: 24,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant={'body2'} fontWeight={600}>
                                                {index + 1}
                                            </Typography>
                                        </Box>
                                        <Box className={'color-circle cursor-not-allowed-hover'}>
                                            <NoColorIcon className={'placeholder'}/>
                                            <div className={'circle'} style={{'--color': group?.colorCode}}/>
                                        </Box>
                                        <Typography variant={'body2'} fontWeight={600}>
                                            {group.title}
                                        </Typography>
                                    </Box>
                                    <PartnerDataGrid
                                        minimizeToolbar
                                        // hideToolbar
                                        classNames={{toolbar: 'pt-0', layout: 'mt-2'}}
                                        hideFooter
                                        hideExporting
                                        {...dataGridSaveConfig}
                                        initialLayoutState={{columns: columns}}
                                        rows={dataGridRows(group)}
                                        visibleRows={5}
                                        columns={dataGridColumns}
                                        state={{
                                            loading: {state: updatingStep},
                                            pagination: {currentPage: 1, length: group.products.length},
                                            density: DataGridDensities.compact,
                                        }}
                                    />
                                </Card>
                            </Grid2>
                        ))
                    }
                </Grid2>
            </Box>
        </>
    );
}

export default PrintBrochureViewReviewSection;
